<template>
  <div>
    <div class="row pt-3 l-m-b-20">
      <div class="col-9">
        <h1>Customers</h1>
      </div>
      <div class="col-3 text-right" v-if="$_can('companies.customers.store')">
        <div class="dropdown dropdown-menu-right pr-3">
          <button class="btn btn-primary" type="button" id="dropdownMenuButton"
                  data-toggle="dropdown" aria-haspopup="true"
                  aria-expanded="false"
          >
            Manage Customers
            <i class="fa fa-caret-down pl-2" />
          </button>
          <div class="dropdown-menu dropdown-menu-right" aria-labelledby="dropdownMenuButton">
            <div class="dropdown-box border-bottom pointer" @click="showCreateSidebar = !showCreateSidebar">
              <button class="dropdown-item">
                Add Customer
              </button>
              <p>Add a single customer for the selected company.</p>
            </div>
            <div class="dropdown-box border-bottom pointer" @click="showImportCustomers = !showImportCustomers">
              <button class="dropdown-item">
                Import Customers
              </button>
              <p>Add multiple customers for the selected company from a file of your choosing.</p>
            </div>
            <div class="dropdown-box border-bottom pointer" @click="openExportModal">
              <button class="dropdown-item">
                Export Customers
              </button>
              <p>Export customers data for all, or selected, companies within a date range of your choice.</p>
            </div>
            <div class="dropdown-box border-bottom pointer" @click="showRequestReviews = !showRequestReviews">
              <button class="dropdown-item">
                Request reviews and optional import
              </button>
              <p>Select customers you wish to receive a review request for the selected company. With this option you can also import customers.</p>
            </div>
            <div class="dropdown-box border-bottom pointer" @click="$router.push('customers/pending')">
              <button class="dropdown-item">
                Pending requests
              </button>
              <p>Check the pending review requests you've sent and their status.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="row">
      <div class="col-12 l-m-b-20">
        <div class="input-group search">
          <input class="form-control" type="text" v-model="q" @keyup="searchText($event)" placeholder="Search" />
          <button type="button" class="btn input-group-btn" @click="clearSearch">
            <i :class="[q ? 'fa fa-remove' : 'fa fa-search']" />
          </button>
        </div>
      </div>
      <div class="col-12 filters-field" />
    </div>
    <template v-if="!customers">
      <vue-content-loading :width="800" :height="450">
        <rect v-for="x in 15" :key="x" x="10" :y="15*x*1.4" rx="5" ry="5" width="720" height="6" />
      </vue-content-loading>
    </template>
    <template v-else>
      <table class="table m-table table-hover l-m-t-20">
        <thead class="m-table__heading">
          <tr>
            <th class="m-table__id">ID</th>
            <th @click="sortBy('users.first_name')" :class="{ active: sortKey === 'users.first_name' }" class="sortable">
              Name
              <span class="arrow" :class="sortOrders['users.first_name'] ? 'dsc' : 'asc'" />
            </th>
            <th @click="sortBy('users.email')" :class="{ active: sortKey === 'users.email' }" class="sortable">
              Email
              <span class="arrow" :class="sortOrders['users.email'] ? 'dsc' : 'asc'" />
            </th>
            <th>Phone</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="customer in customers" @mouseup="$_can('companies.customers.show') && view($event, customer.id)"
              :key="customer.id" class="pointer"
          >
            <td>{{ customer.id }}</td>
            <td>
              <span>{{ customer.first_name }}</span>
              <span>{{ customer.last_name && customer.last_name }}</span>
            </td>
            <td>{{ customer.email }}</td>
            <td>{{ customer.phone && phoneMask.maskedPhone(customer.phone) }}</td>
            <td>{{ customer.user_status.name }}</td>
          </tr>
          <tr v-if="customers && !customers.length">
            <td colspan="6" class="text-center">
              No results found
            </td>
          </tr>
        </tbody>
      </table>

      <div class="row">
        <div class="col-sm-6 mb-1 text-black-50"
             v-if="meta.total"
        >
          Showing {{ meta.from }} - {{ meta.to }} of {{ meta.total }}
        </div>
        <pagination class="col-sm-6 mb-1" :change-page="changePage" :meta="meta" />
      </div>
    </template>

    <!-- Create Customer -->
    <transition name="slide-fade">
      <div class="l-modal-mask" v-if="showCreateSidebar" @click.self="showCreateSidebar = false">
        <customers-create class="sidebar-right" @cancel="showCreateSidebar = false"
                          @update="getCustomers"
        />
      </div>
    </transition>

    <!-- Import customers -->
    <modal v-if="showImportCustomers" @close="closeModal" size="modal-large">
      <template v-if="!importLoading && !successfulImport">
        <div slot="header">
          <h4>Import Customers</h4>
        </div>
        <div slot="body">
          <form enctype="multipart/form-data" @submit.prevent="importCustomers">
            <div class="row mb-4">
              <div class="col">
                <span class="field-heading">select company</span>
                <div class="select-wrap">
                  <label>Company</label>
                  <simple-select v-model="companyId"
                                 :options="companies"
                  />
                </div>
              </div>
            </div>
            <div class="row mb-4">
              <div class="col d-flex flex-column">
                <span class="field-heading">file for import</span>
                <label class="custom-file-upload">
                  <i class="fa fa-cloud-upload" /> Upload file
                  <input class="mt-2"
                         type="file"
                         ref="customer_file"
                         @change="customerFile"
                         accept=".xls,.xlsx,.csv,.tsv,.ods,.slk,.xml"
                  />
                </label>
                <span class="file-name" v-if="uploadedFile && uploadedFile.length">
                  <i class="fa fa-file" /> {{ uploadedFile[0].name }}
                </span>
              </div>
            </div>
            <div class="row mb-4">
              <div class="col">
                <span class="allowed-file-types mb-4">
                  Allowed file types are: CSV, XLSX, TSV, ODS, SLK, ODM, XML.
                </span>
                <span class="file-rules">
                  File needs to contain 4 columns: first_name, last_name, email and phone.
                  <br />
                  Order of the columns is not important.
                  <br />
                  Phone number should be in 10 digit format with only numbers.
                  <br />
                  Last name is optional and can be blank.
                  <br />
                  Email or Phone is required. You can have one or the other, or both.
                </span>
              </div>
            </div>
            <div class="row m-0">
              <div class="col-12 d-flex flex-column justify-content-center align-items-center p-0">
                <button type="submit" class="btn btn-primary ld-ext-right w-100"
                        :disabled="isValid"
                >
                  Import Customers
                </button>
                <span @click="goToSendReviews" class="link pointer mt-3">
                  Go to send reviews
                </span>
              </div>
            </div>
          </form>
        </div>
        <div slot="footer" />
      </template>
      <template v-else-if="importLoading">
        <div slot="header" />
        <div slot="body">
          <div class="d-flex flex-column align-items-center justify-content-center mt-5">
            <span class="loader-text">please wait</span>
            <span class="loader-heading mb-3">Customers are being imported.</span>
            <span class="loader" />
          </div>
        </div>
        <div slot="footer" />
      </template>
      <template v-else-if="successfulImport && !importLoading">
        <div slot="header" />
        <div slot="body">
          <div class="d-flex flex-column align-items-center mt-5 mb-4">
            <div class="d-flex flex-row import-stats">
              <div class="imported col-3">
                {{ importResponse.imported }} Imported
              </div>
              <div class="failed col-3">
                {{ importResponse.failed }} Failed
              </div>
              <div class="errors col-3">
                {{ importResponse.errors }} Errors
              </div>
            </div>
            <div class="d-flex flex-column mt-5 pl-0 col-12">
              <h4>Failed cases</h4>
              <div class="scrollable-body">
                <span v-for="(error, index) in importResponse.failures" :key="index">
                  <span class="font-weight-bold pr-4">ROW {{ index }}</span>
                  <span v-for="(item, itemIndex) in error" :key="itemIndex">
                    <span v-for="(failure, failureIndex) in item" :key="failureIndex"> {{ failure }}</span>
                  </span>
                  <br />
                </span>
              </div>
            </div>
          </div>
        </div>
        <div slot="footer" />
      </template>
    </modal>

    <!-- Export -->
    <modal v-if="showExportModal" @close="closeExportModal" size="modal-large">
      <div slot="header">
        <h4>
          Select export parameters
        </h4>
      </div>
      <div slot="body">
        <div class="row mb-4">
          <div class="col">
            <div class="select-wrap">
              <label>Company</label>
              <simple-select v-model="exportFilters.companyId"
                             :options="companies"
              />
            </div>
          </div>
        </div>
        <div class="row mb-4">
          <div class="col">
            <div class="select-wrap">
              <label>Date</label>
              <flat-pickr v-model="exportFilters.created_at"
                          :config="config"
                          class="simple-select-input"
                          placeholder="Select Date"
                          name="date"
                          @on-close="onCreatedDateChange"
              />
            </div>
          </div>
        </div>
      </div>
      <div slot="footer" class="container">
        <div class="row mb-2">
          <div class="col d-flex justify-content-between">
            <button type="button" class="btn btn-primary" @click="exportCustomers">
              Export
            </button>
          </div>
        </div>
      </div>
    </modal>

    <!--  Request Reviews  -->
    <modal v-if="showRequestReviews" @close="closeModal" size="modal-large">
      <template v-if="!requestLoading && !successfulRequest && !importingRequests">
        <div slot="header">
          <h4>
            Send review request for old customers
          </h4>
        </div>
        <div slot="body">
          <div class="row mb-4">
            <div class="col">
              <span class="field-heading">select company</span>
              <div class="select-wrap">
                <label>Company</label>
                <simple-select v-model="companyId"
                               :options="companies"
                />
              </div>
            </div>
          </div>
          <div class="row mb-4">
            <div class="col">
              <span class="field-heading">customers</span>
              <div v-for="(field, index) in requestsToSend"
                   :key="`em-${index}`"
              >
                <div class="row mb-2">
                  <div class="col">
                    <div class="select-wrap">
                      <label>First Name</label>
                      <input type="text"
                             autocomplete="false"
                             class="form-control"
                             v-model="field.first_name"
                      />
                    </div>
                  </div>
                  <div class="col">
                    <div class="select-wrap">
                      <label>Last Name</label>
                      <input type="text"
                             autocomplete="false"
                             class="form-control"
                             v-model="field.last_name"
                      />
                    </div>
                  </div>
                  <div class="col">
                    <div class="select-wrap">
                      <label>Email</label>
                      <input type="email"
                             autocomplete="false"
                             class="form-control"
                             v-model="field.email"
                      />
                    </div>
                  </div>
                  <div class="col-1 pt-3">
                    <div class="btn btn-clear"
                         v-if="index > 0 || requestsToSend.length !== 1"
                         @click="removeField(index)"
                    >
                      <i class="fa fa-remove" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="row mt-2 mb-5">
            <div class="col-12">
              <span @click="addField" class="btn add-button pointer mr-2">
                <span>
                  <i aria-hidden="true" class="fa fa-plus" />
                </span>Add one more
              </span>
              or use
              <span class="btn add-button pointer ml-2" @click="importingRequests = true">
                <span>
                  <i aria-hidden="true" class="fa fa-plus" />
                </span>Import option
              </span>
            </div>
          </div>
          <div class="row mt-5">
            <div class="col-12 d-flex flex-column justify-content-start">
              <div class="check-field mb-2">
                <label :class="[{'check-open' : createCustomers }]">
                  <span class="checkmark" />Also create these customers and store them for selected Company
                  <input type="checkbox" v-model="createCustomers"
                         :value="createCustomers"
                         class="btn-check"
                  />
                </label>
              </div>
              <button @click="sendRequestReviews" class="btn btn-primary w-100 send-button pull-right ld-ext-right">
                Send
              </button>
            </div>
          </div>
        </div>
        <div slot="footer" />
      </template>
      <template v-else-if="!requestLoading && !successfulRequest && importingRequests">
        <div slot="header">
          <h4>
            Send review request for old customers
          </h4>
        </div>
        <div slot="body">
          <form enctype="multipart/form-data" @submit.prevent="sendRequestReviewsWithFile">
            <div class="row mb-4">
              <div class="col">
                <span class="field-heading">select company</span>
                <div class="select-wrap">
                  <label>Company</label>
                  <simple-select v-model="companyId"
                                 :options="companies"
                  />
                </div>
              </div>
            </div>
            <div class="row mb-4">
              <div class="col d-flex flex-column">
                <span class="field-heading">file for import</span>
                <label class="custom-file-upload">
                  <i class="fa fa-cloud-upload" /> Upload file
                  <input class="mt-2"
                         type="file"
                         ref="request_reviews"
                         @change="requestReviewsFile"
                         accept=".xls,.xlsx,.csv,.tsv,.ods,.slk,.xml"
                  />
                </label>
                <span class="file-name" v-if="uploadedFile && uploadedFile.length">
                  <i class="fa fa-file" /> {{ uploadedFile[0].name }}
                </span>
              </div>
            </div>
            <div class="row mb-4">
              <div class="col">
                <span class="allowed-file-types mb-4">
                  Allowed file types are: CSV, XLSX, TSV, ODS, SLK, ODM, XML.
                </span>
                <span class="file-rules mb-4">
                  File needs to contain 4 columns: first_name, last_name, email and phone.
                  <br />
                  Order of the columns is not important.
                  <br />
                  Phone number should be in 10 digit format with only numbers.
                  <br />
                  Last name is optional and can be blank.
                  <br />
                  Email or Phone is required. You can have one or the other, or both.
                </span>
                <span class="file-rules">
                  Or you can <span class="rules-link pl-1 pr-1" @click="importingRequests = false"> go back </span> to add one by one.
                </span>
              </div>
            </div>
            <div class="row m-0">
              <div class="col-12 d-flex flex-column justify-content-start p-0">
                <div class="check-field mb-2">
                  <label :class="[{'check-open' : createCustomers }]">
                    <span class="checkmark" />Also create these customers and store them for selected Company
                    <input type="checkbox" v-model="createCustomers"
                           :value="createCustomers"
                           class="btn-check"
                    />
                  </label>
                </div>
                <button type="submit" class="btn btn-primary send-button w-100 pull-right ld-ext-right">
                  Send
                </button>
              </div>
            </div>
          </form>
        </div>
        <div slot="footer" />
      </template>
      <template v-else-if="requestLoading">
        <div slot="header" />
        <div slot="body">
          <div class="d-flex flex-column align-items-center justify-content-center mt-5">
            <span class="loader-text">please wait</span>
            <span class="loader-heading mb-3">Request reviews are being imported.</span>
            <span class="loader" />
          </div>
        </div>
        <div slot="footer" />
      </template>
    </modal>
  </div>
</template>

<script>
import axios from 'axios';
import toastr from 'toastr';
import { mapGetters, mapMutations, mapState, mapActions } from 'vuex';
import * as moment from 'moment';
import FileDownload from 'js-file-download';
import FlatPickr from 'vue-flatpickr-component/src/component';
import Pagination from '../Pagination';
import CustomersCreate from './CustomersCreate';
import PhoneMask from '../../classes/PhoneMask';
import Modal from '../Modal';

export default {
  name: 'Customers',
  components: {
    CustomersCreate,
    FlatPickr,
    Pagination,
    Modal,
  },
  data: () => ({
    exportFilters: [
      {
        companyId: '',
        createdDateEnd: '',
        createdDateStart: '',
        created_at: [],
      },
    ],
    config: {
      dateFormat: 'm/j/Y',
      mode: 'range',
    },
    page: '',
    q: '',
    sortKey: '',
    sortOrders: [],
    showExportModal: false,
    companyId: '',
    importResponse: [],
    uploadedFile: '',
    showCreateSidebar: false,
    showImportCustomers: false,
    importLoading: false,
    successfulImport: false,
    showRequestReviews: false,
    requestLoading: false,
    successfulRequest: false,
    importingRequests: false,
    createCustomers: false,
    requestsToSend: [
      {
        first_name: '',
        last_name: '',
        email: '',
      },
    ],
    phoneMask: new PhoneMask(),
  }),
  computed: {
    ...mapState({
      companies: state => state.companies.companies.data,
      customers: state => state.customers.customers.data,
      meta: state => state.customers.customers.meta,
    }),
    ...mapGetters(['getCustomersFiltered']),
    isValid() {
      return this.companyId.length && this.uploadedFile;
    },
  },
  mounted() {
    this.getCustomers();
  },
  methods: {
    ...mapMutations(['setCustomers']),
    ...mapActions(['getCustomers']),
    closeExportModal() {
      this.showExportModal = false;
    },
    openExportModal() {
      this.showExportModal = true;
    },
    sendRequestReviewsWithFile() {
      this.requestLoading = true;
      const formData = new FormData();
      formData.append('company_id', this.companyId);
      formData.append('file', this.$refs.request_reviews.files[0]);
      formData.append('store', this.createCustomers);
      axios
        .post('/customers/review-requests', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })
        .catch(err => this.errors.record(err.response.data.error))
        .finally(() => {
          this.requestLoading = false;
          this.closeModal();
          toastr.success('Successfully created review requests');
        });
    },
    sendRequestReviews() {
      this.requestLoading = true;
      axios
        .post('/customers/review-requests', {
          company_id: this.companyId,
          customers: this.requestsToSend,
          store: this.createCustomers,
        })
        .catch(err => this.errors.record(err.response.data.error))
        .finally(() => {
          this.requestLoading = false;
          this.closeModal();
        });
    },
    importCustomers() {
      this.importLoading = true;
      const formData = new FormData();
      formData.append('company_id', this.companyId);
      formData.append('file', this.$refs.customer_file.files[0]);
      axios
        .post('customers/import', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })
        .then(res => {
          this.successfulImport = true;
          this.importResponse = res.data.data;
        })
        .catch(err => this.errors.record(err.response.data.error))
        .finally(() => {
          this.importLoading = false;
        });
    },
    goToSendReviews() {
      this.showImportCustomers = false;
      this.showRequestReviews = true;
    },
    requestReviewsFile() {
      this.uploadedFile = this.$refs.request_reviews.files;
    },
    customerFile() {
      this.uploadedFile = this.$refs.customer_file.files;
    },
    view(event, id) {
      if (event.button === 1) {
        window.open(this.$router.resolve(`/customers/edit/${id}`).href, '_blank');
      } else {
        this.$router.push(`/customers/edit/${id}`);
      }
    },
    changePage(pageNum) {
      this.setCustomers({});
      this.page = pageNum;
      this.filter();
    },
    searchText(e) {
      this.page = 1;
      if (/^[a-zA-Z0-9]$/.test(e.key) || e.keyCode === 8 || e.keyCode === 13) {
        const vm = this;
        clearTimeout(this.timer);
        this.timer = setTimeout(() => {
          vm.filter();
        }, 500);
      }
    },
    search() {
      this.page = 1;
      this.filter();
    },
    filter() {
      this.getCustomersFiltered(
        this.page,
        this.q,
        this.sortKey,
        this.sortOrders[this.sortKey] ? 'asc' : 'desc',
      );
    },
    clearSearch() {
      this.q = '';
      this.filter();
    },
    sortBy(key) {
      this.sortKey = key;
      this.$set(this.sortOrders, key, !this.sortOrders[key]);
      this.filter();
    },
    closeModal() {
      this.showImportCustomers = false;
      this.successfulImport = false;
      this.companyId = '';
      this.importResponse = [];
      this.uploadedFile = '';
      this.showRequestReviews = false;
      this.requestLoading = false;
      this.successfulRequest = false;
      this.requestsToSend = [
        {
          first_name: '',
          last_name: '',
          email: '',
        },
      ];
      this.createCustomers = false;
      this.importingRequests = false;
      this.getCustomers();
    },
    addField() {
      this.requestsToSend.push({
        first_name: '',
        last_name: '',
        email: '',
      });
    },
    removeField(index) {
      this.requestsToSend.splice(index, 1);
    },
    exportCustomers() {
      const data = {
        company_id: this.exportFilters.companyId,
        created_from: this.exportFilters.createdDateStart,
        created_to: this.exportFilters.createdDateEnd,
      };
      axios.get(`customers/export`, { params: data, responseType: 'blob' }).then(response => {
        let fileName = `Customers ${moment().format('YYYY-MM-DD')}`;
        fileName += '.csv';
        FileDownload(response.data, fileName);
        toastr.success('Successfully created file');
        this.closeExportModal();
      });
    },
    onCreatedDateChange(selectedDates) {
      this.exportFilters.createdDateStart = selectedDates[0]
        ? selectedDates[0].toLocaleDateString('en-US')
        : '';
      this.exportFilters.createdDateEnd = selectedDates[1]
        ? selectedDates[1].toLocaleDateString('en-US')
        : '';
    },
  },
};
</script>
<style lang="scss" scoped>
.scrollable-body {
  max-height: 400px;
  overflow: auto;
}
.field-heading {
  font-weight: 500;
  font-size: 12px;
  letter-spacing: 0.2px;
  text-transform: uppercase;
  color: #616161;
}
.allowed-file-types {
  font-weight: bold;
  font-size: 10px;
  display: flex;
  align-items: center;
  color: #4c5669;
}
.file-rules {
  font-size: 10px;
  display: flex;
  align-items: center;
}
.rules-link {
  text-decoration-line: underline;
  color: #2962ff;
  cursor: pointer;
}
.link {
  font-size: 14px;
  line-height: 24px;
  display: flex;
  align-items: center;
  text-decoration-line: underline;
  color: #2962ff;
}
.loader {
  margin-bottom: 30px;
  border: 3px solid rgba(0, 0, 0, 0);
  border-left-color: #2962ff;
  animation: loader-spin 1.2s infinite linear;
  position: relative;
  display: inline-block;
  vertical-align: top;
  border-radius: 50%;
  width: 45px;
  height: 45px;
}
.loader-text {
  font-size: 12px;
  text-transform: uppercase;
  color: #808d98;
}
.loader-heading {
  font-size: 18px;
  text-align: center;
  color: #616161;
}
@keyframes loader-spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
input[type='file'] {
  display: none;
}
.custom-file-upload {
  border: 1px solid #ccc;
  display: inline-block;
  padding: 19px 12px;
  cursor: pointer;
  width: 100%;
  border-radius: 4px;
}
.import-stats {
  width: 100%;
  justify-content: space-between;
}
.imported {
  background: rgba(102, 168, 15, 0.2);
  border: 1px solid #66a80f;
  box-sizing: border-box;
  border-radius: 4px;
  font-weight: 600;
  font-size: 18px;
  text-align: center;
  color: #66a80f;
  padding: 8px 20px;
}
.failed {
  background: rgba(255, 171, 0, 0.2);
  border: 1px solid #ffab00;
  box-sizing: border-box;
  border-radius: 4px;
  font-weight: 600;
  font-size: 18px;
  text-align: center;
  color: #ffab00;
  padding: 8px 20px;
}
.errors {
  background: rgba(255, 0, 0, 0.2);
  border: 1px solid #ff0000;
  box-sizing: border-box;
  border-radius: 4px;
  font-weight: 600;
  font-size: 18px;
  text-align: center;
  color: #ff0000;
  padding: 8px 20px;
}
.dropdown-menu {
  max-height: 356px !important;
}
.send-button {
  min-width: 170px;
}
.file-name {
  color: #40c057;
  font-weight: bold;
}
.check-field label {
  line-height: 20px;
}
</style>



// WEBPACK FOOTER //
// src/components/customers/Customers.vue